import {lazy} from './lang.js';
import {
    applyToLeaf,
    applyRule,
    before,
    after,
    when ,
    ACCENT,
    STACCATO,
    REST,
    PIANO,
    PIANISSIMO
} from './trules.js';
import {getLeafString, dash, dot} from '../tidal/parser.js';
import {tparse} from './tidal.js';
import {concat} from './list.js';
import {t_leaf, t_swap, t_transform_leaf, t_map} from './tlist.js';

export const decisions = (n) => {
    return (...args) => {
        let _n = lazy(n, ...args);

        let decisions = (Math.round(_n) >>> 0).toString(2)
            .split('')
            .map(x => parseInt(x));
      
        let decisionCounter = 0;
        let decide = () => decisions[(decisionCounter++) % decisions.length];
        
        let decideNum = (len, start=0) => {
            for (let i=len; i > start; i-- ){
                if (decide()) {
                    return i;
                }
            }
            return start;
        };

        let decideUnique = (count, total) => {
            total = Math.min(count, total);
            let arr = new Array(total).fill(0).map((a,i) => i);
            let added = [];
            while (added.length < count) {
                for (let i=0; i < arr.length; i++) {
                    if (decide()) {
                        added.push(arr[i]);
                        arr.splice(i, 1);
                        break;
                    }
                }
            }
            return added;
        };

        return {decide, decideNum, decideUnique};
    };
};

export const t_generate = (pattern, n, variable=12, factor=1, ...terms) => {
    let lastPattern = undefined;
    let lastN = undefined;
    let lazyValue = undefined;
    return (...args) => {
        let _pattern = lazy(pattern, ...args);
        let _n = lazy(n, ...args);
        let _factor  = lazy(factor, ...args);
        let _variable = lazy(variable, ...args);
        let _terms = terms.map(term => lazy(term || 2, ...args));

        if (_pattern === lastPattern && lastN === _n) {
            return lazyValue;
        }
        
        lastPattern = _pattern;
        lastN = _n;
        // create decision list from input
        let {decideNum, decide} = decisions(_n)(...args);       

        // decide the hit transpose
        let hitTranspose = 12; 
        
        // returns [transformedTerm, newCounter]
        // transforms a term given the algo using the decision lisst
        let iter_term = (term, i) => {
            let _term = [term];
            // decide: replace 1st term w rest?
            if (decide()) {
                _term = [tparse('~')()[0]];
            } else if (decide()) {
                term.accent = true;
            }
            
            // decide how many terms? 1,2,3,4
            let len = _factor * decideNum(_terms[i%_terms.length] || 2, 1);
            
            for (let i=1; i < len; i++) {
                _term.push(tparse('~')()[0]);
            }
            
            // decide where to place hit
            let whereToPlaceHit = decideNum(len - 1, 1);

            hitTranspose = decide() ? _variable : 0;
            let hitString = decide() ?
                new String(hitTranspose) :
                new String(hitTranspose) + '\'';
            _term[whereToPlaceHit] = tparse(
                hitString)()[0];
            
            return concat(_term)();
        };
        
        lazyValue = t_map(
            pattern,
            (x,i) => iter_term(x,i))(...args);
        return lazyValue;
    };
    
};

export const t_simplify = (pattern, percent) => {

    let lastPattern = undefined;
    let lastPercent = undefined;
    let lazyValue = undefined;
    return (...args) => {
        let _pattern = lazy(pattern, ... args);
        let _percent = lazy(percent, ...args);

        if (_pattern === lastPattern && lastPercent === _percent) {
            return lazyValue || [];
        }
        lastPattern = _pattern;
        lastPercent = _percent;

        if (_percent === 100) {
            return _pattern || [];
        }

        _percent = 100 - _percent;

        
        let totalLength = _pattern.flat(10).filter(
            x => x.transpose !== null).length;
        let triggersToKill = totalLength * _percent/100;

        let c=0;
        let index=0;
        while (c <= triggersToKill && index <= totalLength) {
            _pattern = t_transform_leaf(
                _pattern,
                getIndex(totalLength - c, index),
                (x, arr, i) => {
                    c++;
                    return tparse('~')()[0];
                },
                undefined,
                true
            )(...args);
            index++;
        }
        lazyValue = _pattern[0];
        return lazyValue;
    };
};

export const t_syncopate = (pattern, n=0) => {
    let lastPattern = undefined;
    let lastN = undefined;
    let lazyValue = undefined;

    return (...args) => {
        let _p = lazy(pattern, ...args);
        let _n = lazy(n, ...args);

        if (lastN === _n && _p === lastPattern) {
            return lazyValue;
        }
        lastN = _n;
        lastPattern = _p;
        let {
            decide, decideNum, decideUnique
        } = decisions(n)(...args);    
        
        let t_express = (_term) => {
            let num = decideNum(4);
            let uniques = decideUnique(num, 4);
            let accents = 0;
            let rests = 0;
            uniques.forEach(
                index => {
                    index = index % _term.length;
                    let element = _term[index];
                    if (Array.isArray(element)) return;
                    let copy = {
                        ... element
                    };
                    
                    if (!decide() && (accents++) < 1) {
                        if (copy.transpose === null && decide() && (rests++ < 1)) {
                            copy = tparse('0\'')();
                        } else {
                            copy.accent = true;
                        }
                    } else {
                        if (copy.transpose === null && decide() && (rests++ < 1)) {
                            copy = tparse('0.')();
                        } else {
                            copy.staccato = true;
                        }
                    }
                    _term[index] = copy;
                });
            return _term;
        }
        
        let iter_term = (term, i) => {
            if (decide() && decide()) {
                return t_express(t_swap(term, [null, tparse('0.')]));
            }
            if (!Array.isArray(term)) {
                term = {
                    ... term,
                };
            }
            let _term = term;
            if (!Array.isArray(term)) {
                _term = [term];
            } else {
                _term = [term[decideNum(term.length - 1)]];
            }        
            // first decide whether it will be 2 or 4 terms
            let rest = tparse('~')(...args)[0];
            if (term.transpose === null) {
                term.transpose = 0;
            }
            
            let leaf = Array.isArray(term) ? term[0] : term;
            
            if (decide()) {
                _term = [rest, leaf];
            } else {
                _term = [rest, leaf, leaf, leaf];
                let [d1, d2, d3, d4] = [
                    decide(),
                    decide() && decide(),
                    decide(),
                    decide() && decide()];
                
                d1 && (_term[0] = leaf);
                d2 && (_term[1] = rest);
                d2 && (_term[2] = rest);
                d3 && (_term[3] = rest);
                
                if (!d1 && !d2 && !d3 && !d4) {
                    _term[decideNum(3)] = leaf;
                }
                
                let num = decideNum(4);
                let uniques = decideUnique(num, 4);
                let accents = 0;
                let rests = 0;
                uniques.forEach(
                    index => {
                        let element = _term[index];
                        if (Array.isArray(element)) return;
                        let copy = {
                            ... element
                        };
                        
                        if (!decide() && (accents++) < 1) {
                            if (copy.transpose === null && decide() && (rests++ < 1)) {
                                copy = tparse('0\'')();
                            } else {
                                copy.accent = true;
                            }
                            
                        } else {
                            if (copy.transpose === null && decide() && (rests++ < 1)) {
                                copy = tparse('0.')();
                            } else {
                                copy.staccato = true;
                            }
                        }
                        _term[index] = copy;
                    });
            }
            return _term;
        };
        
        lazyValue = t_map(
            pattern,
            (x,i) => iter_term(x,i))(...args);

        return lazyValue;
    };
};

/**
   0 1 2 3 | 4 5 6 7
   
   0 -> 1
   1 -> 5
   2 -> 3
 */
const getIndex = (totalLength, index) => {
    let half = Math.ceil(totalLength / 2);
    if (totalLength % 2 === 0) {
        half++;
    }
    let _add = 1;
    let add = (index + _add) % half;
    if (index % 2 === 0) {
        return add;
    } else {
        return (half + add) % totalLength;
    }
}
